import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getCurrencyRate } from '@/lib/api';

const STORAGE_KEY = 'lalita_currency';
const FALLBACK_RATE = 16000; // USD → IDR fallback if API fails

const CurrencyContext = createContext(null);

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrencyState] = useState(() => {
    if (typeof window === 'undefined') return 'USD';
    return localStorage.getItem(STORAGE_KEY) || 'USD';
  });
  const [rate, setRate] = useState(FALLBACK_RATE);
  const [rateLoaded, setRateLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getCurrencyRate()
      .then((res) => {
        if (cancelled) return;
        const r = res?.data?.usd_to_idr;
        if (r && r > 0) setRate(r);
      })
      .catch(() => {})
      .finally(() => !cancelled && setRateLoaded(true));
    return () => { cancelled = true; };
  }, []);

  const setCurrency = useCallback((c) => {
    const next = c === 'IDR' ? 'IDR' : 'USD';
    setCurrencyState(next);
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, next);
    }
  }, []);

  const toggle = useCallback(() => {
    setCurrency(currency === 'USD' ? 'IDR' : 'USD');
  }, [currency, setCurrency]);

  /**
   * Format a price stored in USD according to the active currency.
   * Pass `currencyOverride` to force USD/IDR irrespective of context.
   */
  const formatPrice = useCallback((usdPrice, currencyOverride = null) => {
    if (usdPrice === null || usdPrice === undefined || usdPrice === 0 || isNaN(usdPrice)) {
      return 'Price on Request';
    }
    const c = currencyOverride || currency;
    if (c === 'IDR') {
      const idr = Math.round(usdPrice * rate);
      if (idr >= 1_000_000_000) return `Rp ${(idr / 1_000_000_000).toFixed(2)}B`;
      if (idr >= 1_000_000) return `Rp ${(idr / 1_000_000).toFixed(1)}M`;
      if (idr >= 1_000) return `Rp ${(idr / 1_000).toFixed(0)}K`;
      return `Rp ${idr.toLocaleString('id-ID')}`;
    }
    // USD
    if (usdPrice >= 1_000_000) return `$${(usdPrice / 1_000_000).toFixed(1)}M`;
    return `$${Math.round(usdPrice).toLocaleString('en-US')}`;
  }, [currency, rate]);

  /**
   * Compact label for filter dropdowns: $100K, $1M, Rp 1.6B, etc.
   * Always uses abbreviated K/M/B even for small values.
   */
  const formatPriceShort = useCallback((usdPrice, currencyOverride = null) => {
    if (usdPrice === null || usdPrice === undefined || isNaN(usdPrice)) return '';
    const c = currencyOverride || currency;
    if (c === 'IDR') {
      const idr = usdPrice * rate;
      if (idr >= 1_000_000_000) return `Rp ${(idr / 1_000_000_000).toFixed(idr % 1_000_000_000 === 0 ? 0 : 1)}B`;
      if (idr >= 1_000_000) return `Rp ${Math.round(idr / 1_000_000)}M`;
      if (idr >= 1_000) return `Rp ${Math.round(idr / 1_000)}K`;
      return `Rp ${Math.round(idr)}`;
    }
    if (usdPrice >= 1_000_000) return `$${(usdPrice / 1_000_000).toFixed(usdPrice % 1_000_000 === 0 ? 0 : 1)}M`;
    if (usdPrice >= 1_000) return `$${Math.round(usdPrice / 1_000)}K`;
    return `$${Math.round(usdPrice)}`;
  }, [currency, rate]);

  /**
   * IDR formatter used when the property has an IDR-anchored price.
   * Renders the stored IDR amount as-is (no rate math, so it's static
   * across rate fluctuations).
   */
  const formatIDR = useCallback((idr) => {
    if (idr === null || idr === undefined || idr === 0 || isNaN(idr)) return null;
    if (idr >= 1_000_000_000) return `Rp ${(idr / 1_000_000_000).toFixed(2)}B`;
    if (idr >= 1_000_000) return `Rp ${(idr / 1_000_000).toFixed(1)}M`;
    if (idr >= 1_000) return `Rp ${(idr / 1_000).toFixed(0)}K`;
    return `Rp ${idr.toLocaleString('id-ID')}`;
  }, []);

  /**
   * Format a property price respecting IDR-anchored storage.
   * - `idrPrice` (anchor) takes priority. When the user is viewing IDR, the
   *    anchor is shown verbatim (static). When viewing USD, USD is derived
   *    live from the anchor.
   * - When no IDR anchor exists, falls back to USD-anchored behaviour.
   */
  const formatPropertyPrice = useCallback((usdPrice, idrPrice, currencyOverride = null) => {
    const c = currencyOverride || currency;
    if (idrPrice && idrPrice > 0) {
      if (c === 'IDR') return formatIDR(idrPrice);
      // USD view: derive from the IDR anchor using the live rate
      if (!rate || rate <= 0) return 'Price on Request';
      const derivedUsd = idrPrice / rate;
      if (derivedUsd >= 1_000_000) return `$${(derivedUsd / 1_000_000).toFixed(1)}M`;
      return `$${Math.round(derivedUsd).toLocaleString('en-US')}`;
    }
    return formatPrice(usdPrice, currencyOverride);
  }, [currency, rate, formatPrice, formatIDR]);

  const value = {
    currency,
    setCurrency,
    toggle,
    rate,
    rateLoaded,
    formatPrice,
    formatPriceShort,
    formatPropertyPrice,
    formatIDR,
  };

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used inside CurrencyProvider');
  return ctx;
};
